import React, { useState } from 'react';
import { brandColors } from '../data/brandColors';
import {
  EyeOff,
  Lightbulb,
  MessageSquare,
  Compass,
  Settings,
  Gauge,
  BookOpen,
  ArrowRight,
  ArrowDown,
  Circle,
  Square,
  Triangle,
  Hexagon,
  Layers,
  Network,
  AlertTriangle,
  RotateCcw,
  Shield,
  FileText,
  Palette,
  Grid
} from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './priorities-ai-design/ProductDesignDocPrimitives';

export const VisualDesignGrammar: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<'colors' | 'shapes' | 'lines' | 'fills' | 'patterns' | 'connections' | 'encoding'>('colors');

  // Visual Grammar System
  const visualGrammar = {
    colors: {
      primary: {
        name: brandColors.primary.name,
        hex: brandColors.primary.hex,
        meaning: 'Framework Structure',
        usage: 'Main framework elements, structural components',
        examples: ['Ladder rungs', 'Logical level headers', 'Johari quadrants']
      },
      secondary: {
        name: brandColors.secondary.name,
        hex: brandColors.secondary.hex,
        meaning: 'Health & Readiness',
        usage: 'Status indicators, healthy states, ready conditions',
        examples: ['Ready status', 'Healthy development', 'Positive feedback']
      },
      accent: {
        name: brandColors.accent.name,
        hex: brandColors.accent.hex,
        meaning: 'Learning & Discovery',
        usage: 'High learning value, discovery moments, insights',
        examples: ['Learning indicators', 'Discovery highlights', 'Key insights']
      },
      coral: {
        name: brandColors.coral.name,
        hex: brandColors.coral.hex,
        meaning: 'Unknown & Uncertainty',
        usage: 'Unknown elements, uncertainty, blind spots',
        examples: ['Unknown self', 'Uncertainty indicators', 'Blind spots']
      },
      neutral: {
        name: 'Granite Gray',
        hex: '#9CA3AF',
        meaning: 'Neutral Base',
        usage: 'Neutral structures, dividers, subtle surfaces',
        examples: ['Card borders', 'Secondary text', 'UI scaffolding']
      },
      brown: {
        name: 'Warm Brown',
        hex: '#584E47',
        meaning: 'Earth & Material',
        usage: 'Terrain, craftsmanship, grounded accent',
        examples: ['Path elements', 'Handmade accents', 'Earth textures']
      }
    },
    shapes: {
      circle: {
        icon: <Circle className="h-6 w-6" />,
        meaning: 'State or Context',
        usage: 'Current conditions, states of being, contexts',
        examples: ['Current state', 'Context markers', 'Status indicators']
      },
      rectangle: {
        icon: <Square className="h-6 w-6" />,
        meaning: 'Process or Activity',
        usage: 'Actions, processes, activities, behaviors',
        examples: ['Behavior level', 'Action steps', 'Process stages']
      },
      diamond: {
        icon: <Triangle className="h-6 w-6" />,
        meaning: 'Decision Point',
        usage: 'Choices, decisions, branching points',
        examples: ['Decision moments', 'Choice points', 'Branching logic']
      },
      hexagon: {
        icon: <Hexagon className="h-6 w-6" />,
        meaning: 'System or Framework',
        usage: 'Complete systems, frameworks, holistic views',
        examples: ['Complete frameworks', 'System boundaries', 'Holistic views']
      }
    },
    lines: {
      solid: {
        style: 'border-solid',
        meaning: 'Direct Causality',
        usage: 'Direct cause and effect relationships',
        examples: ['Data → Conclusion', 'Identity → Behavior', 'Known → Known']
      },
      dashed: {
        style: 'border-dashed',
        meaning: 'Influence or Suggestion',
        usage: 'Indirect influence, suggestions, soft connections',
        examples: ['Influence arrows', 'Suggestion lines', 'Soft connections']
      },
      dotted: {
        style: 'border-dotted',
        meaning: 'Potential or Possibility',
        usage: 'Potential paths, possibilities, future states',
        examples: ['Potential paths', 'Future possibilities', 'Hypothetical connections']
      },
      thick: {
        style: 'border-4',
        meaning: 'Strong Connection',
        usage: 'Strong relationships, primary connections',
        examples: ['Primary relationships', 'Strong connections', 'Main pathways']
      }
    },
    fills: {
      solid: {
        style: 'bg-opacity-100',
        meaning: 'Certainty',
        usage: 'Known, certain, established elements',
        examples: ['Known facts', 'Certain states', 'Established elements']
      },
      light: {
        style: 'bg-opacity-20',
        meaning: 'Partial Knowledge',
        usage: 'Partially known, developing understanding',
        examples: ['Developing understanding', 'Partial knowledge', 'Emerging states']
      },
      gradient: {
        style: 'bg-gradient-to-r',
        meaning: 'Transition or Change',
        usage: 'Transitions, changes, evolving states',
        examples: ['State transitions', 'Evolving understanding', 'Change processes']
      },
      transparent: {
        style: 'bg-transparent',
        meaning: 'Unknown or Hidden',
        usage: 'Unknown, hidden, or undiscovered elements',
        examples: ['Unknown elements', 'Hidden aspects', 'Undiscovered states']
      }
    },
    patterns: {
      hierarchical: {
        icon: <Layers className="h-6 w-6" />,
        meaning: 'Levels of Influence',
        usage: 'Shows levels, hierarchy, influence cascades',
        examples: ['Logical Levels', 'Ladder of Inference', 'Organizational hierarchy']
      },
      matrix: {
        icon: <Grid className="h-6 w-6" />,
        meaning: 'Two-Dimensional Analysis',
        usage: 'Shows relationships between two dimensions',
        examples: ['Johari Window', 'Skill vs. Interest matrix', 'Risk vs. Impact grid']
      },
      flow: {
        icon: <ArrowRight className="h-6 w-6" />,
        meaning: 'Process Flow',
        usage: 'Shows sequence, process, movement',
        examples: ['Decision process', 'Change sequence', 'Workflow']
      },
      nested: {
        icon: <Circle className="h-6 w-6" />,
        meaning: 'Containment or Layers',
        usage: 'Shows containment, layers, nested systems',
        examples: ['Nested systems', 'Layered understanding', 'Contained elements']
      }
    },
    connections: {
      direct: {
        icon: <ArrowRight className="h-4 w-4" />,
        meaning: 'Direct Cause → Effect',
        usage: 'Direct causal relationships',
        examples: ['Data → Conclusion', 'Identity → Behavior', 'Known → Known']
      },
      feedback: {
        icon: <RotateCcw className="h-4 w-4" />,
        meaning: 'Feedback Loop',
        usage: 'Circular influence, feedback mechanisms',
        examples: ['Learning loops', 'Feedback cycles', 'Reinforcing patterns']
      },
      influence: {
        icon: <ArrowDown className="h-4 w-4" />,
        meaning: 'Influence or Suggestion',
        usage: 'Indirect influence, suggestions',
        examples: ['Influence arrows', 'Suggestion lines', 'Soft connections']
      },
      threshold: {
        icon: <Shield className="h-4 w-4" />,
        meaning: 'Threshold or Boundary',
        usage: 'Boundaries, thresholds, transition points',
        examples: ['State boundaries', 'Transition points', 'Threshold markers']
      }
    }
  };

  // Rich Encoding System
  const encodingSystem = {
    assumptions: {
      icon: <AlertTriangle className="h-6 w-6" />,
      color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]',
      meaning: 'Assumptions we tend to make',
      usage: 'Highlight common assumptions that can lead to errors',
      examples: [
        'Assuming others see what we see',
        'Assuming past patterns predict future',
        'Assuming one size fits all solutions',
        'Assuming silence means agreement'
      ],
      visualMarkers: {
        icon: '⚠️',
        border: 'border-[var(--mustard)] border-dashed',
        background: 'bg-[var(--mustard-dim)]',
        text: 'text-[var(--mustard)]'
      }
    },
    looseDefinitions: {
      icon: <MessageSquare className="h-6 w-6" />,
      color: 'bg-coral-100 border-coral-300 text-coral-800',
      meaning: 'Words that are loosely defined and prone to misunderstanding',
      usage: 'Flag terms that need clarification or definition',
      examples: [
        'Leadership (what kind?)',
        'Success (by whose measure?)',
        'Communication (effective how?)',
        'Change (from what to what?)'
      ],
      visualMarkers: {
        icon: '💬',
        border: 'border-coral-300 border-dotted',
        background: 'bg-coral-50',
        text: 'text-coral-800'
      }
    },
    importantTips: {
      icon: <Lightbulb className="h-6 w-6" />,
      color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]',
      meaning: 'Very important tips or pieces of information',
      usage: 'Highlight critical insights that can make or break success',
      examples: [
        'Start with data, not conclusions',
        'Ask "What am I not seeing?"',
        'Context determines everything',
        'One size never fits all'
      ],
      visualMarkers: {
        icon: '💡',
        border: 'border-[var(--mustard)] border-2',
        background: 'bg-[var(--mustard-dim)]',
        text: 'text-[var(--mustard)]'
      }
    },
    fieldDeckContext: {
      icon: <BookOpen className="h-6 w-6" />,
      color: 'bg-sage-100 border-sage-300 text-sage-800',
      meaning: 'Part of a field card deck on increasing self-knowledge and awareness',
      usage: 'Indicate this is part of a larger learning system',
      examples: [
        'Self-Knowledge Deck',
        'Awareness Toolkit',
        'Coaching Essentials',
        'Leadership Fundamentals'
      ],
      visualMarkers: {
        icon: '📚',
        border: 'border-sage-300',
        background: 'bg-sage-50',
        text: 'text-sage-800'
      }
    },
    relatedReading: {
      icon: <FileText className="h-6 w-6" />,
      color: 'bg-consulting-slate-blue-100 border-consulting-slate-blue-300 text-consulting-slate-blue-800',
      meaning: 'Related reading and resources',
      usage: 'Point to additional learning materials',
      examples: [
        'See: Ladder of Inference',
        'Read: Johari Window Guide',
        'Explore: Logical Levels',
        'Study: Systems Thinking'
      ],
      visualMarkers: {
        icon: '📖',
        border: 'border-consulting-slate-blue-300',
        background: 'bg-consulting-slate-blue-50',
        text: 'text-consulting-slate-blue-800'
      }
    },
    visualConnections: {
      icon: <Network className="h-6 w-6" />,
      color: 'bg-sage-100 border-sage-300 text-sage-800',
      meaning: 'Visual that shows how topics are connected',
      usage: 'Reveal relationships between concepts',
      examples: [
        '2x2 Matrix (Johari Window)',
        'Hierarchical List (Ladder of Inference)',
        'Pyramid (Logical Levels)',
        'Flow Diagram (Process)'
      ],
      visualMarkers: {
        icon: '🔗',
        border: 'border-sage-300 border-dashed',
        background: 'bg-sage-50',
        text: 'text-sage-800'
      }
    },
    contextMatters: {
      icon: <Compass className="h-6 w-6" />,
      color: 'bg-coral-100 border-coral-300 text-coral-800',
      meaning: 'Context matters here - not the same answer every time',
      usage: 'Indicate situational awareness is required',
      examples: [
        'Depends on the situation',
        'Context determines approach',
        'No universal answer',
        'Situational awareness needed'
      ],
      visualMarkers: {
        icon: '🧭',
        border: 'border-coral-300 border-dotted',
        background: 'bg-coral-50',
        text: 'text-coral-800'
      }
    },
    dialNotToggle: {
      icon: <Gauge className="h-6 w-6" />,
      color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]',
      meaning: 'This is really a dial and not a toggle',
      usage: 'Indicate continuous rather than binary states',
      examples: [
        'Scale of 1-10',
        'Continuum, not either/or',
        'Gradual change',
        'Range of possibilities'
      ],
      visualMarkers: {
        icon: '🎚️',
        border: 'border-[var(--mustard)] border-2',
        background: 'bg-[var(--mustard-dim)]',
        text: 'text-[var(--mustard)]'
      }
    },
    offTheRecord: {
      icon: <EyeOff className="h-6 w-6" />,
      color: 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-pri)]',
      meaning: 'Off the record - sensitive or private information',
      usage: 'Mark confidential or sensitive content',
      examples: [
        'Confidential',
        'Private reflection',
        'Internal use only',
        'Sensitive topic'
      ],
      visualMarkers: {
        icon: '🔒',
        border: 'border-[var(--rule)] border-dashed',
        background: 'bg-[var(--surface)]',
        text: 'text-[var(--text-pri)]'
      }
    }
  };

  // Tool-Specific Applications
  const toolApplications = {
    ladder: {
      name: 'Ladder of Inference',
      description: 'Cognitive framework for understanding how we move from data to action',
      visualElements: {
        colors: ['primary', 'secondary', 'accent'],
        shapes: ['rectangle', 'diamond'],
        lines: ['solid', 'thick'],
        fills: ['solid', 'light'],
        patterns: ['hierarchical', 'flow'],
        connections: ['direct', 'threshold']
      },
      symbolicMeaning: {
        primary: 'Framework structure - the ladder itself',
        secondary: 'Healthy thinking - questioning assumptions',
        accent: 'Learning moments - discovering blind spots',
        coral: 'Unknown data - what we don\'t see'
      }
    },
    johari: {
      name: 'Johari Window',
      description: 'Framework for understanding self-awareness and self-disclosure',
      visualElements: {
        colors: ['primary', 'secondary', 'accent', 'coral'],
        shapes: ['rectangle', 'circle'],
        lines: ['solid', 'dashed'],
        fills: ['solid', 'transparent'],
        patterns: ['matrix'],
        connections: ['influence', 'threshold']
      },
      symbolicMeaning: {
        primary: 'Known framework - the window structure',
        secondary: 'Healthy disclosure - open communication',
        accent: 'Learning opportunity - feedback and discovery',
        coral: 'Unknown self - what no one knows'
      }
    },
    logical: {
      name: 'Logical Levels',
      description: 'Hierarchical framework for understanding change at different levels',
      visualElements: {
        colors: ['primary', 'secondary', 'accent'],
        shapes: ['hexagon', 'circle', 'rectangle'],
        lines: ['solid', 'thick'],
        fills: ['solid', 'gradient'],
        patterns: ['hierarchical', 'nested'],
        connections: ['direct', 'influence']
      },
      symbolicMeaning: {
        primary: 'Framework structure - the levels themselves',
        secondary: 'Healthy development - balanced growth',
        accent: 'Learning transformation - deep change',
        coral: 'Unknown potential - undiscovered levels'
      }
    }
  };

  const renderColorSystem = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Color System</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(visualGrammar.colors).map(([key, color]) => (
          <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-12 h-12 border border-[var(--rule)]"
                style={{ backgroundColor: color.hex }}
              ></div>
              <div>
                <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{color.name}</h4>
                <p className="text-sm text-[var(--text-sec)]">{color.meaning}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-[var(--text-sec)]">Usage:</div>
              <p className="text-sm text-[var(--text-sec)]">{color.usage}</p>
              <div className="text-sm font-medium text-[var(--text-sec)]">Examples:</div>
              <ul className="text-sm text-[var(--text-sec)] space-y-1">
                {color.examples.map((example, index) => (
                  <li key={index}>• {example}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderShapeSystem = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Shape System</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(visualGrammar.shapes).map(([key, shape]) => (
          <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 border border-[var(--rule)] bg-[var(--surface-raised)] flex items-center justify-center text-[var(--text-sec)]">
                {shape.icon}
              </div>
              <div>
                <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)] capitalize">{key}</h4>
                <p className="text-sm text-[var(--text-sec)]">{shape.meaning}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-[var(--text-sec)]">Usage:</div>
              <p className="text-sm text-[var(--text-sec)]">{shape.usage}</p>
              <div className="text-sm font-medium text-[var(--text-sec)]">Examples:</div>
              <ul className="text-sm text-[var(--text-sec)] space-y-1">
                {shape.examples.map((example, index) => (
                  <li key={index}>• {example}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLineSystem = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Line System</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(visualGrammar.lines).map(([key, line]) => (
          <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-4 mb-4">
              {/* Line grammar specimen — border-style/width demonstrates the line meaning */}
              <div className={`w-12 h-2 rounded ${line.style} border-2 border-[var(--text-sec)]`}></div>
              <div>
                <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)] capitalize">{key}</h4>
                <p className="text-sm text-[var(--text-sec)]">{line.meaning}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-[var(--text-sec)]">Usage:</div>
              <p className="text-sm text-[var(--text-sec)]">{line.usage}</p>
              <div className="text-sm font-medium text-[var(--text-sec)]">Examples:</div>
              <ul className="text-sm text-[var(--text-sec)] space-y-1">
                {line.examples.map((example, index) => (
                  <li key={index}>• {example}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderFillSystem = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Fill System</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(visualGrammar.fills).map(([key, fill]) => (
          <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-4 mb-4">
              {/* Fill grammar specimen — fill.style (opacity/gradient/transparent) demonstrates the fill meaning */}
              <div className={`w-12 h-12  border border-[var(--rule)] bg-[var(--slate)] ${fill.style}`}></div>
              <div>
                <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)] capitalize">{key}</h4>
                <p className="text-sm text-[var(--text-sec)]">{fill.meaning}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-[var(--text-sec)]">Usage:</div>
              <p className="text-sm text-[var(--text-sec)]">{fill.usage}</p>
              <div className="text-sm font-medium text-[var(--text-sec)]">Examples:</div>
              <ul className="text-sm text-[var(--text-sec)] space-y-1">
                {fill.examples.map((example, index) => (
                  <li key={index}>• {example}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPatternSystem = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Pattern System</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(visualGrammar.patterns).map(([key, pattern]) => (
          <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 border border-[var(--rule)] bg-[var(--surface-raised)] flex items-center justify-center text-[var(--text-sec)]">
                {pattern.icon}
              </div>
              <div>
                <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)] capitalize">{key}</h4>
                <p className="text-sm text-[var(--text-sec)]">{pattern.meaning}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-[var(--text-sec)]">Usage:</div>
              <p className="text-sm text-[var(--text-sec)]">{pattern.usage}</p>
              <div className="text-sm font-medium text-[var(--text-sec)]">Examples:</div>
              <ul className="text-sm text-[var(--text-sec)] space-y-1">
                {pattern.examples.map((example, index) => (
                  <li key={index}>• {example}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderConnectionSystem = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Connection System</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(visualGrammar.connections).map(([key, connection]) => (
          <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 border border-[var(--rule)] bg-[var(--surface-raised)] flex items-center justify-center text-[var(--text-sec)]">
                {connection.icon}
              </div>
              <div>
                <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)] capitalize">{key}</h4>
                <p className="text-sm text-[var(--text-sec)]">{connection.meaning}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-[var(--text-sec)]">Usage:</div>
              <p className="text-sm text-[var(--text-sec)]">{connection.usage}</p>
              <div className="text-sm font-medium text-[var(--text-sec)]">Examples:</div>
              <ul className="text-sm text-[var(--text-sec)] space-y-1">
                {connection.examples.map((example, index) => (
                  <li key={index}>• {example}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderEncodingSystem = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Rich Encoding System</h3>
      <p className="text-[var(--text-sec)] mb-6">
        These encoding markers help users quickly identify different types of information and understand context at a glance.
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(encodingSystem).map(([key, encoding]) => (
          <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 border border-[var(--rule)] bg-[var(--surface-raised)] flex items-center justify-center text-[var(--text-sec)]">
                {encoding.icon}
              </div>
              <div>
                <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</h4>
                <p className="text-sm text-[var(--text-sec)]">{encoding.meaning}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium text-[var(--text-sec)] mb-1">Usage:</div>
                <p className="text-sm text-[var(--text-sec)]">{encoding.usage}</p>
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-sec)] mb-1">Examples:</div>
                <ul className="text-sm text-[var(--text-sec)] space-y-1">
                  {encoding.examples.map((example, index) => (
                    <li key={index}>• {example}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-sec)] mb-2">Visual Markers:</div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{encoding.visualMarkers.icon}</span>
                  {/* Encoding badge specimen — the color-coded chip is the visual language being documented */}
                  <div className={`px-3 py-1 rounded text-xs font-medium ${encoding.color}`}>
                    {encoding.visualMarkers.icon} Sample Text
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderToolApplications = () => (
    <div className="space-y-6">
      {Object.entries(toolApplications).map(([key, tool]) => (
        <div key={key} className="p-6 border border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 border border-[var(--rule)] bg-[var(--surface-raised)] flex items-center justify-center">
              <Layers className="h-6 w-6 text-[var(--text-sec)]" />
            </div>
            <div>
              <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)]">{tool.name}</h4>
              <p className="text-sm text-[var(--text-sec)]">{tool.description}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-md font-heading font-medium text-[var(--text-sec)] mb-2">Visual Elements Used:</h5>
              <div className="space-y-2">
                {Object.entries(tool.visualElements).map(([category, elements]) => (
                  <div key={category}>
                    <span className="text-sm font-medium text-[var(--text-sec)] capitalize">{category}:</span>
                    <span className="text-sm text-[var(--text-ter)] ml-2">{elements.join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-md font-heading font-medium text-[var(--text-sec)] mb-2">Symbolic Meaning:</h5>
              <div className="space-y-2">
                {Object.entries(tool.symbolicMeaning).map(([color, meaning]) => (
                  <div key={color} className="flex items-center gap-2">
                    {/* Color grammar specimen — swatch driven by brandColors hex */}
                    <div
                      className="w-4 h-4 rounded border border-[var(--rule)]"
                      style={{ backgroundColor: visualGrammar.colors[color as keyof typeof visualGrammar.colors]?.hex }}
                    ></div>
                    <span className="text-sm text-[var(--text-sec)]">{meaning}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSelectedCategory = () => {
    switch (selectedCategory) {
      case 'colors': return renderColorSystem();
      case 'shapes': return renderShapeSystem();
      case 'lines': return renderLineSystem();
      case 'fills': return renderFillSystem();
      case 'patterns': return renderPatternSystem();
      case 'connections': return renderConnectionSystem();
      case 'encoding': return renderEncodingSystem();
      default: return renderColorSystem();
    }
  };

  return (
    <div>
      <ProductDesignPageHeader
        title="Visual Design Grammar"
        eyebrow="Layout · cognition"
        backTo="/design-language"
        backLabel="Design language"
        subtitle="A systematic approach to visual communication that encodes meaning through colors, shapes, lines, fills, patterns, and connections."
      />

      <ProductDesignSection title="Grammar categories">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
          {[
            { id: 'colors', name: 'Colors', icon: <Palette className="h-5 w-5" /> },
            { id: 'shapes', name: 'Shapes', icon: <Square className="h-5 w-5" /> },
            { id: 'lines', name: 'Lines', icon: <ArrowRight className="h-5 w-5" /> },
            { id: 'fills', name: 'Fills', icon: <Circle className="h-5 w-5" /> },
            { id: 'patterns', name: 'Patterns', icon: <Layers className="h-5 w-5" /> },
            { id: 'connections', name: 'Connections', icon: <Network className="h-5 w-5" /> },
            { id: 'encoding', name: 'Encoding', icon: <Settings className="h-5 w-5" /> }
          ].map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id as any)}
              className={`p-4  border transition-colors text-center ${
                selectedCategory === category.id
                  ? 'border-[var(--slate)] bg-[var(--surface-raised)] text-[var(--text-pri)]'
                  : 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                {category.icon}
                <span className="text-sm font-medium">{category.name}</span>
              </div>
            </button>
          ))}
        </div>
      </ProductDesignSection>

      <ProductDesignSection title="Grammar system">
        {renderSelectedCategory()}
      </ProductDesignSection>

      <ProductDesignSection title="Tool applications">
        {renderToolApplications()}
      </ProductDesignSection>

      <ProductDesignSection title="Usage guidelines">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">Principles</h3>
            <ul className="space-y-2 text-sm text-[var(--text-sec)]">
              <li>• <strong>Consistency:</strong> Use the same visual elements to represent the same concepts</li>
              <li>• <strong>Clarity:</strong> Visual elements should clarify, not confuse</li>
              <li>• <strong>Context:</strong> Meaning depends on context and combination</li>
              <li>• <strong>Hierarchy:</strong> Use visual weight to show importance</li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)]">Applications</h3>
            <ul className="space-y-2 text-sm text-[var(--text-sec)]">
              <li>• <strong>Coaching:</strong> Help clients see patterns and connections</li>
              <li>• <strong>Training:</strong> Create memorable visual frameworks</li>
              <li>• <strong>Communication:</strong> Encode complex ideas visually</li>
              <li>• <strong>Analysis:</strong> Reveal hidden relationships and patterns</li>
            </ul>
          </div>
        </div>
      </ProductDesignSection>
    </div>
  );
};
